'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/modules/auth/contexts/AuthContext'
import { useHsRecentlyViewed } from '@/modules/hs/hooks/useHsRecentlyViewed'

export type AccessContext = {
    type: 'view-hs-detail'
    hsCode: string
}

export type AccessAction = 'none' | 'blur-paywall' | 'hide'

export interface AccessStatus {
    isAllowed: boolean
    action: AccessAction
    reason: string
    isLoading: boolean
}

const isDevOrLocal = process.env.NODE_ENV === "development";

export function useAccessControl(context: AccessContext): AccessStatus {
    const { isAuthenticated, isLoading: authLoading } = useAuth()
    const { items: recentItems, isLoaded: itemsLoaded } = useHsRecentlyViewed()

    const [status, setStatus] = useState<AccessStatus>({
        isAllowed: true,
        action: 'none',
        reason: '',
        isLoading: true, // always start as loading until both auth and storage are ready
    })

    useEffect(() => {
        // Wait for both auth and items from localStorage to be ready to avoid bypass loophole
        if (authLoading || !itemsLoaded) {
            setStatus({
                isAllowed: true, // Temporarily allow to prevent flicker
                action: 'none',
                reason: '',
                isLoading: true,
            });
            return;
        }

        let allowed = true
        let action: AccessAction = 'none'
        let reason = ''

        // Rule 1: Limit unauthenticated users to viewing 5 distinct HS codes
        if (context.type === 'view-hs-detail') {
            if (!isAuthenticated && !isDevOrLocal) {
                // Chỉ lấy 5 mã HS đã xem mới nhất làm danh sách miễn phí
                const FREE_VIEW_LIMIT = 5;
                const allowedCodes = new Set(recentItems.slice(0, FREE_VIEW_LIMIT).map((item) => item.hsCode))

                // Nếu mã hiện tại không nằm trong TOP 5 mã đã xem, và đã đủ giới hạn 5 mã
                if (allowedCodes.has(context.hsCode) && recentItems.length >= FREE_VIEW_LIMIT) {
                    allowed = false
                    action = 'blur-paywall'
                    reason = `Bạn đã xem đủ 5 mã HS.\nĐăng nhập hoặc Tạo tài khoản để tiếp tục truy cập dữ liệu mã HS`
                }
            }
        }

        // Các rule tương lai có thể thêm ở đây
        // ví dụ: nếu cần check VIP tier:
        // if (context.type === 'some-premium-feature' && user.tier !== 'PRO') { ... }

        setStatus({
            isAllowed: allowed,
            action,
            reason,
            isLoading: false,
        })
    }, [authLoading, itemsLoaded, isAuthenticated, recentItems, context.type, context.hsCode])

    return status
}
